import React, { useState, useRef, useEffect } from 'react';
import {
  Modal, Button, Select, Tooltip, Popover, Input, Space, Segmented,
} from 'antd';
import {
  Bold, Italic, Underline, Type, Image, Save, Eye, EyeOff, Plus,
} from 'react-feather';
import { DEFAULT_ITALIAN_TEMPLATE } from './italianCertificateTemplate';
import {
  ITALIAN_MERGE_FIELDS,
  italianTextToEditableHtml,
  serializeEditable,
  insertItalianMergeFieldAtCursor,
} from './italianMergeFieldUtils';
import './templateEditor.css';
import './italianCertificate.css';
import sigJeanBaptiste from './sig-jean-baptiste.png';
import sigBortone from './sig-bortone.png';
import sigCaserta from './sig-caserta.png';

const EditableLine = ({
  value, onChange, className, allowMergeFields = false, previewMode, onFocus, tag: TagName = 'div', editorLocale = 'fr',
}) => {
  const ref = useRef(null);
  const isFocused = useRef(false);

  useEffect(() => {
    if (ref.current && !isFocused.current) {
      ref.current.innerHTML = italianTextToEditableHtml(value, previewMode, allowMergeFields, editorLocale);
    }
  }, [value, previewMode, allowMergeFields, editorLocale]);

  return (
    <TagName
      ref={ref}
      className={`cierha-wysiwyg-editable ${className || ''}`}
      contentEditable
      suppressContentEditableWarning
      onInput={() => ref.current && onChange(serializeEditable(ref.current))}
      onFocus={() => { isFocused.current = true; onFocus?.(ref); }}
      onBlur={() => { isFocused.current = false; }}
    />
  );
};

const ItalianTemplateEditorModal = ({
  open, onClose, onSave, template, defaultAssets,
}) => {
  const [draft, setDraft] = useState(DEFAULT_ITALIAN_TEMPLATE);
  const [previewMode, setPreviewMode] = useState(true);
  const [activeEditorRef, setActiveEditorRef] = useState(null);
  const [logoPopover, setLogoPopover] = useState(null);
  const [editorLocale, setEditorLocale] = useState('fr');

  useEffect(() => {
    if (open) setDraft({ ...DEFAULT_ITALIAN_TEMPLATE, ...template });
  }, [open, template]);

  const updateAsset = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const updateLocale = (key, value) => {
    const localeKey = editorLocale === 'it' ? 'italian' : 'french';
    setDraft((prev) => ({
      ...prev,
      [localeKey]: { ...prev[localeKey], [key]: value },
    }));
  };

  const localeDraft = editorLocale === 'it' ? draft.italian : draft.french;

  const execFormat = (cmd) => {
    document.execCommand(cmd, false, null);
    activeEditorRef?.current?.focus();
  };

  const handleInsertField = (key) => {
    if (activeEditorRef?.current) {
      activeEditorRef.current.focus();
      insertItalianMergeFieldAtCursor(key);
      activeEditorRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const leftLogo = draft.leftLogoUrl || defaultAssets.leftLogo;
  const rightLogo = draft.rightLogoUrl || defaultAssets.rightLogo;
  const borderImg = draft.borderImageUrl || defaultAssets.border;

  const logoPopoverContent = (side) => (
    <div className="cierha-logo-popover">
      <Input
        placeholder="URL (vide = image par défaut)"
        value={side === 'left' ? draft.leftLogoUrl : draft.rightLogoUrl}
        onChange={(e) => updateAsset(side === 'left' ? 'leftLogoUrl' : 'rightLogoUrl', e.target.value)}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="98vw"
      style={{ top: 8, paddingBottom: 0 }}
      className="cierha-word-modal"
      footer={null}
      destroyOnClose
      closable={false}
    >
      <div className="cierha-word-app">
        <div className="cierha-word-titlebar">
          <span className="cierha-word-docname">Modèle — Certificat de Langue Italienne</span>
          <button type="button" className="cierha-word-close" onClick={onClose}>×</button>
        </div>

        <div className="cierha-word-ribbon">
          <div className="cierha-ribbon-group">
            <span className="cierha-ribbon-label">Langue</span>
            <Segmented
              value={editorLocale}
              onChange={setEditorLocale}
              options={[
                { label: 'Français', value: 'fr' },
                { label: 'Italien', value: 'it' },
              ]}
            />
          </div>
          <div className="cierha-ribbon-divider" />
          <div className="cierha-ribbon-group">
            <span className="cierha-ribbon-label">Police</span>
            <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('bold')}><Bold size={15} /></button>
            <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('italic')}><Italic size={15} /></button>
            <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('underline')}><Underline size={15} /></button>
          </div>
          <div className="cierha-ribbon-divider" />
          <div className="cierha-ribbon-group">
            <span className="cierha-ribbon-label">Insérer</span>
            <Select
              className="cierha-field-select"
              placeholder="Champ de fusion"
              style={{ width: 200 }}
              onChange={handleInsertField}
              value={null}
              suffixIcon={<Plus size={14} />}
              options={ITALIAN_MERGE_FIELDS.map((f) => ({ value: f.key, label: f.label }))}
            />
            <Tooltip title={previewMode ? 'Afficher {{champ}}' : 'Aperçu exemple'}>
              <button type="button" className={`cierha-ribbon-btn ${previewMode ? 'active' : ''}`} onClick={() => setPreviewMode((p) => !p)}>
                {previewMode ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            </Tooltip>
          </div>
          <div className="cierha-ribbon-spacer" />
          <Space>
            <Popover
              content={(
                <div className="cierha-logo-popover">
                  <Input
                    placeholder="URL bordure"
                    value={draft.borderImageUrl}
                    onChange={(e) => updateAsset('borderImageUrl', e.target.value)}
                  />
                </div>
              )}
              title="Image de bordure"
              trigger="click"
            >
              <Button icon={<Image size={15} />}>Bordure</Button>
            </Popover>
            <Button onClick={onClose}>Annuler</Button>
            <Button type="primary" icon={<Save size={15} />} onClick={() => onSave(draft)}>Enregistrer</Button>
          </Space>
        </div>

        <div className="cierha-word-workspace">
          <div className="cierha-word-page-area">
            <div className="italian-cert-page italian-cert-page-editor">
              <img src={borderImg} alt="" className="italian-cert-border" aria-hidden="true" />
              <div className="italian-cert-content">
                <div className="italian-cert-header">
                  <Popover content={logoPopoverContent('left')} trigger="click" open={logoPopover === 'left'} onOpenChange={(v) => setLogoPopover(v ? 'left' : null)}>
                    <div className="cierha-wysiwyg-logo-wrap">
                      <img src={leftLogo} alt="CIREP" className="italian-cert-logo italian-cert-logo-left" />
                      <div className="cierha-wysiwyg-logo-overlay"><Image size={14} /> Modifier</div>
                    </div>
                  </Popover>
                  <Popover content={logoPopoverContent('right')} trigger="click" open={logoPopover === 'right'} onOpenChange={(v) => setLogoPopover(v ? 'right' : null)}>
                    <div className="cierha-wysiwyg-logo-wrap">
                      <img src={rightLogo} alt="Troia" className="italian-cert-logo italian-cert-logo-right" />
                      <div className="cierha-wysiwyg-logo-overlay"><Image size={14} /> Modifier</div>
                    </div>
                  </Popover>
                </div>

                <EditableLine value={localeDraft.documentTitle} onChange={(v) => updateLocale('documentTitle', v)} className="italian-cert-title" onFocus={setActiveEditorRef} tag="h1" editorLocale={editorLocale} />

                <EditableLine value={localeDraft.deliverPrefix} onChange={(v) => updateLocale('deliverPrefix', v)} className="italian-cert-deliver" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />

                <p className="italian-cert-name-preview">
                  {editorLocale === 'fr' && (
                    <>
                      <span className="cierha-merge-field cierha-merge-field-static" contentEditable={false}>{'{{genre}}'}</span>
                      {' '}
                    </>
                  )}
                  <span className="cierha-merge-field cierha-merge-field-static" contentEditable={false}>{'{{noms}}'}</span>
                </p>

                <EditableLine
                  value={localeDraft.birthLineTemplate}
                  onChange={(v) => updateLocale('birthLineTemplate', v)}
                  className="italian-cert-birth"
                  allowMergeFields
                  previewMode={previewMode}
                  onFocus={setActiveEditorRef}
                  tag="p"
                  editorLocale={editorLocale}
                />

                <EditableLine
                  value={localeDraft.bodyTemplate}
                  onChange={(v) => updateLocale('bodyTemplate', v)}
                  className="italian-cert-body"
                  allowMergeFields
                  previewMode={previewMode}
                  onFocus={setActiveEditorRef}
                  tag="p"
                  editorLocale={editorLocale}
                />

                <EditableLine
                  value={localeDraft.dateLineTemplate}
                  onChange={(v) => updateLocale('dateLineTemplate', v)}
                  className="italian-cert-date"
                  allowMergeFields
                  previewMode={previewMode}
                  onFocus={setActiveEditorRef}
                  tag="p"
                  editorLocale={editorLocale}
                />

                <div className="italian-cert-signatures italian-cert-signatures-three">
                  <div className="italian-cert-sig-col">
                    <EditableLine value={localeDraft.signature1Name} onChange={(v) => updateLocale('signature1Name', v)} className="italian-cert-sig-name" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <EditableLine value={localeDraft.signature1Title} onChange={(v) => updateLocale('signature1Title', v)} className="italian-cert-sig-title" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <img src={sigJeanBaptiste} alt="" className="italian-cert-sig-image italian-cert-sig-image-bap" aria-hidden="true" />
                  </div>
                  <div className="italian-cert-sig-col">
                    <EditableLine value={localeDraft.signature2Name} onChange={(v) => updateLocale('signature2Name', v)} className="italian-cert-sig-name" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <EditableLine value={localeDraft.signature2Title} onChange={(v) => updateLocale('signature2Title', v)} className="italian-cert-sig-title" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <img src={sigBortone} alt="" className="italian-cert-sig-image italian-cert-sig-image-bortone" aria-hidden="true" />
                  </div>
                  <div className="italian-cert-sig-col">
                    <EditableLine value={localeDraft.signature3Name} onChange={(v) => updateLocale('signature3Name', v)} className="italian-cert-sig-name" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <EditableLine value={localeDraft.signature3Title} onChange={(v) => updateLocale('signature3Title', v)} className="italian-cert-sig-title" onFocus={setActiveEditorRef} tag="p" editorLocale={editorLocale} />
                    <img src={sigCaserta} alt="" className="italian-cert-sig-image italian-cert-sig-image-caserta" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="cierha-word-sidebar">
            <div className="cierha-sidebar-section">
              <h4><Type size={14} /> Champs de fusion</h4>
              <p className="cierha-sidebar-hint">Cliquez pour insérer dans le paragraphe actif.</p>
              <div className="cierha-field-list">
                {ITALIAN_MERGE_FIELDS.map((field) => (
                  <button key={field.key} type="button" className="cierha-field-chip" onClick={() => handleInsertField(field.key)}>
                    <code>{`{{${field.key}}}`}</code>
                    <span>{field.label}</span>
                  </button>
                ))}
                {editorLocale === 'it' && (
                  <button type="button" className="cierha-field-chip" onClick={() => handleInsertField('birthVerb')}>
                    <code>{'{{birthVerb}}'}</code>
                    <span>Nato / Nata</span>
                  </button>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
};

export default ItalianTemplateEditorModal;
